/** @format */

import { Grid } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  GA4_COMPONENT_NAME,
  GA4_COMPONENT_POSITION,
  GA4_COMPONENT_TYPE
} from 'src/analytics/componentInteractionGa4';
import { Button } from 'src/components';
import Breadcrumb from 'src/components/Breadcrumb';
import Page from 'src/components/Page';
import useGa4Analytics from 'src/hooks/useGa4Analytics';
import useQuery from 'src/hooks/useQuery';
import { useDispatch, useSelector } from 'src/store';
import { getEventArticles } from 'src/slices/events';
import { EventItem, EventListSkeleton } from 'src/components/events/item';

const startViewTransition = callback => {
  if (document.startViewTransition) {
    document.startViewTransition(() => {
      flushSync(callback);
    });
  } else {
    callback();
  }
};

export default function EventsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery().get('query');
  const { t } = useTranslation();
  const { trackComponentInteraction } = useGa4Analytics();
  const { error, eventArticles, eventArticlesLoading } = useSelector(state => state.events);
  const [displayedContent, setDisplayedContent] = useState(() => ({
    loading: Boolean(eventArticlesLoading),
    articles: eventArticles,
    error
  }));
  const prevLoadingRef = useRef(eventArticlesLoading);

  useEffect(() => {
    dispatch(
      getEventArticles({
        query: query || ''
      })
    );
  }, [dispatch, query]);

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = eventArticlesLoading;

    if (wasLoading && !eventArticlesLoading) {
      startViewTransition(() => {
        setDisplayedContent({ loading: false, articles: eventArticles, error });
      });
    } else if (eventArticlesLoading && !wasLoading) {
      setDisplayedContent(prevState => ({
        loading: true,
        articles:
          Array.isArray(prevState.articles) && prevState.articles.length > 0 ? prevState.articles : eventArticles,
        error: null
      }));
    } else if (!eventArticlesLoading) {
      setDisplayedContent({ loading: false, articles: eventArticles, error });
    }
  }, [eventArticlesLoading, eventArticles, error]);

  const pageTitle = t('events.title');
  const displayedArticles = Array.isArray(displayedContent.articles) ? displayedContent.articles : [];
  const hasDisplayedArticles = displayedArticles.length > 0;
  const shouldShowSkeleton = displayedContent.loading && !hasDisplayedArticles;
  const shouldDimArticles = displayedContent.loading && hasDisplayedArticles;

  const handleBackToHomeClick = useCallback(() => {
    trackComponentInteraction({
      component_name: GA4_COMPONENT_NAME.BACK_TO_HOME,
      component_type: GA4_COMPONENT_TYPE.BUTTON,
      component_position: GA4_COMPONENT_POSITION.EVENTS_LIST
    });
    navigate('/dashboard');
  }, [navigate, trackComponentInteraction]);

  return (
    <Page className={`archive-page events-page`} title={pageTitle}>
      <Breadcrumb data={[{ title: pageTitle }]} additionalClasses="container--narrow" />
      <Grid className="archive-page-container container container--narrow">
        <Grid className="archive-page-title-box events-page-title-box">
          <h1 className="archive-page-title events-page-title">{pageTitle}</h1>
        </Grid>
        <p className="events-page-description">{t('events.description')}</p>

        <Grid
          className={`events-list archive-page-list${shouldDimArticles ? ' events-list--loading' : ''}`}
          style={{ viewTransitionName: 'events-list' }}
          aria-busy={shouldDimArticles}>
          {displayedContent.error ? (
            <p>{t('errorpage.text')}</p>
          ) : shouldShowSkeleton ? (
            <EventListSkeleton count={6} />
          ) : displayedArticles.length > 0 ? (
            displayedArticles.map((item, index) => (
              <EventItem item={item} key={item.nid || item.id || item.link || `${item.title || 'event'}-${index}`} />
            ))
          ) : (
            <p className="events-list-empty">{t('common.noResults')}</p>
          )}
        </Grid>
        <Button onClick={handleBackToHomeClick} borderOnly back label={t('common.button.backToHome')} />
      </Grid>
    </Page>
  );
}
